import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { theme, cardStyle, inputStyle, labelStyle, btnStyle, statCard } from '../theme';
import WhatsAppTemplatesPage from './WhatsAppTemplatesPage';
import WhatsAppContactsPage from './WhatsAppContactsPage';
import WhatsAppCampaignsPage from './WhatsAppCampaignsPage';
import WhatsAppAutoReplyPage from './WhatsAppAutoReplyPage';
import WhatsAppInboxPage from './WhatsAppInboxPage';
import WhatsAppFlowsPage from './WhatsAppFlowsPage';
import WhatsAppLeadFormsPage from './WhatsAppLeadFormsPage';
import WhatsAppApiKeysPage from './WhatsAppApiKeysPage';
import WhatsAppInteractiveFlowsPage from './WhatsAppInteractiveFlowsPage';
import WhatsAppAnalyticsPage from './WhatsAppAnalyticsPage';

const TABS = ['Dashboard', 'Send Message', 'Templates', 'Contacts', 'Campaigns', 'Inbox', 'Auto-Reply', 'Flows', 'Forms', 'API Keys', 'Interactive', 'Analytics', 'Settings'];

const MESSAGE_TEMPLATES = [
  { label: 'Appointment Reminder', text: `Namaste {name} ji!\n\nThis is a reminder for your upcoming appointment at Siddharth Dental Hospital.\n\nDate: {date}\nTime: {time}\n\nPlease arrive 10 minutes early.\nFor queries: 9685058003` },
  { label: 'Treatment Done', text: `Namaste {name} ji!\n\nYour treatment has been completed successfully at Siddharth Dental Hospital.\n\nTreatment: {treatment}\nAmount: {amount}\n\nPlease follow the prescribed instructions carefully.\nFor queries: 9685058003` },
  { label: 'Payment Receipt', text: `Namaste {name} ji!\n\nPayment received at Siddharth Dental Hospital.\n\nAmount: Rs.{amount}\nMode: {mode}\n\nThank you!\nFor queries: 9685058003` },
  { label: 'Birthday Wish', text: `Happy Birthday {name} ji! 🎂\n\nWishing you a wonderful year of smiles from the team at Siddharth Dental Hospital!\n\nFor appointments: 9685058003` },
  { label: 'Recall Reminder', text: `Namaste {name} ji!\n\nIt's time for your routine dental check-up at Siddharth Dental Hospital.\n\nPlease book your appointment at your earliest convenience.\nCall: 9685058003` },
];

const WhatsAppPage = ({ API_BASE, patientsList }) => {
  const [activeTab, setActiveTab] = useState('Dashboard');
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  // Send Message state
  const [recipientMode, setRecipientMode] = useState('patient'); // 'patient' or 'manual'
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [manualPhone, setManualPhone] = useState('');
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);

  // Settings state (loaded from server)
  const [settings, setSettings] = useState({});
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [autoSettings, setAutoSettings] = useState(() => {
    try { return JSON.parse(localStorage.getItem('waAutoSettings') || '{}'); } catch { return {}; }
  });
  const [testingConn, setTestingConn] = useState(false);

  // Load integration config from server (per-tenant, encrypted DB storage)
  useEffect(() => {
    if (activeTab !== 'Settings') return;
    axios.get(`${API_BASE}/saas/integrations/whatsapp`)
      .then(res => {
        const c = res.data?.credentials || {};
        setSettings({
          phoneNumberId: c.phoneNumberId || '',
          accessToken: c.accessToken || '',
          businessAccountId: c.wabaId || '',
          verifyToken: c.verifyToken || '',
          enabled: res.data?.enabled ?? false,
        });
      })
      .catch(() => {});
  }, [activeTab, API_BASE]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Fetch connection status
  const fetchStatus = async () => {
    setStatusLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/whatsapp/status`);
      setStatus(res.data);
    } catch (e) {
      setStatus({ connected: false, error: e.message });
    }
    setStatusLoading(false);
  };

  // Fetch templates
  const fetchTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/whatsapp/templates`);
      setTemplates(res.data?.data || res.data || []);
    } catch (e) {
      setTemplates([]);
    }
    setTemplatesLoading(false);
  };

  useEffect(() => {
    if (activeTab === 'Dashboard') fetchStatus();
    if (activeTab === 'Templates') fetchTemplates();
  }, [activeTab]);

  // Send message handler
  const handleSend = async () => {
    let phone = '';
    if (recipientMode === 'patient') {
      const p = (patientsList || []).find(pt => pt._id === selectedPatientId);
      phone = p?.phone || '';
    } else {
      phone = manualPhone;
    }
    if (!phone) return showToast('Please provide a phone number', 'error');
    if (!messageText.trim()) return showToast('Please enter a message', 'error');

    setSending(true);
    try {
      await axios.post(`${API_BASE}/whatsapp/send-text`, { to: phone, message: messageText });
      // Increment localStorage counter
      const today = new Date().toISOString().slice(0, 10);
      const counters = JSON.parse(localStorage.getItem('waMsgCounters') || '{}');
      counters[today] = (counters[today] || 0) + 1;
      localStorage.setItem('waMsgCounters', JSON.stringify(counters));
      showToast('Message sent successfully!');
      setMessageText('');
    } catch (e) {
      showToast(e.response?.data?.error || e.message || 'Failed to send', 'error');
    }
    setSending(false);
  };

  // Save setting locally; server save happens on "Save to Server" click
  const saveSettings = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const persistSettings = async () => {
    setSettingsSaving(true);
    try {
      const payload = {
        enabled: settings.enabled ?? true,
        credentials: {
          phoneNumberId: settings.phoneNumberId || '',
          accessToken: settings.accessToken || '',
          wabaId: settings.businessAccountId || '',
          verifyToken: settings.verifyToken || '',
        },
      };
      await axios.put(`${API_BASE}/saas/integrations/whatsapp`, payload);
      showToast('Saved securely to server ✓');
    } catch (e) {
      showToast(e.response?.data?.error || 'Save failed', 'error');
    }
    setSettingsSaving(false);
  };

  const saveAutoSetting = (key, value) => {
    const updated = { ...autoSettings, [key]: value };
    setAutoSettings(updated);
    localStorage.setItem('waAutoSettings', JSON.stringify(updated));
  };

  const testConnection = async () => {
    setTestingConn(true);
    try {
      const res = await axios.get(`${API_BASE}/whatsapp/status`);
      if (res.data?.connected) {
        showToast('Connected successfully!');
      } else {
        showToast('Not connected. Check credentials.', 'error');
      }
    } catch (e) {
      showToast('Connection failed: ' + (e.message || ''), 'error');
    }
    setTestingConn(false);
  };

  const todayCount = (() => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const counters = JSON.parse(localStorage.getItem('waMsgCounters') || '{}');
      return counters[today] || 0;
    } catch { return 0; }
  })();

  const tabStyle = (t) => ({
    padding: '10px 20px',
    cursor: 'pointer',
    fontWeight: activeTab === t ? '700' : '500',
    fontSize: '0.9rem',
    color: activeTab === t ? theme.primary : theme.textMuted,
    borderBottom: activeTab === t ? `3px solid ${theme.primary}` : '3px solid transparent',
    background: 'none',
    border: 'none',
    borderBottom: activeTab === t ? `3px solid ${theme.primary}` : '3px solid transparent',
  });

  return (
    <div>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          padding: '12px 24px', borderRadius: theme.radiusSm,
          backgroundColor: toast.type === 'error' ? theme.danger : theme.success,
          color: '#fff', fontWeight: '600', fontSize: '0.9rem',
          boxShadow: theme.shadowMd,
        }}>
          {toast.msg}
        </div>
      )}

      <h2 style={{ margin: '0 0 16px', color: theme.navy, fontSize: '1.4rem' }}>WhatsApp Module</h2>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', borderBottom: `1px solid ${theme.border}`, marginBottom: '20px', overflowX: 'auto', whiteSpace: 'nowrap' }}>
        {TABS.map(t => (
          <button key={t} style={tabStyle(t)} onClick={() => setActiveTab(t)}>{t}</button>
        ))}
      </div>

      {/* ─── DASHBOARD ─── */}
      {activeTab === 'Dashboard' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '20px' }}>
            <div style={{
              ...statCard,
              borderLeft: `4px solid ${status?.connected ? theme.success : theme.danger}`,
            }}>
              <div style={{ fontSize: '0.78rem', color: theme.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>Connection Status</div>
              <div style={{ fontSize: '1.3rem', fontWeight: '700', color: status?.connected ? theme.success : theme.danger }}>
                {statusLoading ? 'Checking...' : status?.connected ? 'Connected' : 'Disconnected'}
              </div>
            </div>

            <div style={statCard}>
              <div style={{ fontSize: '0.78rem', color: theme.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>Phone Number</div>
              <div style={{ fontSize: '1.1rem', fontWeight: '600', color: theme.navy }}>
                {status?.phoneNumber || '—'}
              </div>
            </div>

            <div style={statCard}>
              <div style={{ fontSize: '0.78rem', color: theme.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>Verified Name</div>
              <div style={{ fontSize: '1.1rem', fontWeight: '600', color: theme.navy }}>
                {status?.verifiedName || '—'}
              </div>
            </div>

            <div style={statCard}>
              <div style={{ fontSize: '0.78rem', color: theme.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>Quality Rating</div>
              <div style={{ fontSize: '1.1rem', fontWeight: '600', color: theme.navy }}>
                {status?.qualityRating || '—'}
              </div>
            </div>

            <div style={statCard}>
              <div style={{ fontSize: '0.78rem', color: theme.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>Messages Sent Today</div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: theme.primary }}>
                {todayCount}
              </div>
            </div>
          </div>

          <button
            onClick={fetchStatus}
            style={{ ...btnStyle, marginTop: 0 }}
          >
            Refresh Status
          </button>
        </div>
      )}

      {/* ─── SEND MESSAGE ─── */}
      {activeTab === 'Send Message' && (
        <div style={cardStyle}>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Recipient</label>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.9rem' }}>
                <input type="radio" checked={recipientMode === 'patient'} onChange={() => setRecipientMode('patient')} />
                Select Patient
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.9rem' }}>
                <input type="radio" checked={recipientMode === 'manual'} onChange={() => setRecipientMode('manual')} />
                Manual Phone
              </label>
            </div>

            {recipientMode === 'patient' ? (
              <select
                value={selectedPatientId}
                onChange={e => setSelectedPatientId(e.target.value)}
                style={{ ...inputStyle, width: '100%' }}
              >
                <option value="">-- Select Patient --</option>
                {(patientsList || []).map(p => (
                  <option key={p._id} value={p._id}>{p.name} — {p.phone || 'No phone'}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                placeholder="Enter phone number (e.g. 9876543210)"
                value={manualPhone}
                onChange={e => setManualPhone(e.target.value)}
                style={{ ...inputStyle, width: '100%' }}
              />
            )}
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Quick Templates</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {MESSAGE_TEMPLATES.map(t => (
                <button
                  key={t.label}
                  onClick={() => setMessageText(t.text)}
                  style={{
                    padding: '6px 14px', borderRadius: theme.radiusSm,
                    border: `1px solid ${theme.border}`, background: theme.primaryLight,
                    color: theme.primaryDark, fontSize: '0.82rem', fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Message</label>
            <textarea
              value={messageText}
              onChange={e => setMessageText(e.target.value)}
              rows={8}
              style={{ ...inputStyle, width: '100%', resize: 'vertical', fontFamily: 'inherit' }}
              placeholder="Type your message here..."
            />
          </div>

          <button
            onClick={handleSend}
            disabled={sending}
            style={{ ...btnStyle, marginTop: 0, opacity: sending ? 0.6 : 1, backgroundColor: '#25D366' }}
          >
            {sending ? 'Sending...' : 'Send WhatsApp Message'}
          </button>
        </div>
      )}

      {/* ─── TEMPLATES ─── */}
      {activeTab === 'Templates' && <WhatsAppTemplatesPage />}
      {activeTab === 'Contacts' && <WhatsAppContactsPage />}
      {activeTab === 'Campaigns' && <WhatsAppCampaignsPage />}
      {activeTab === 'Inbox' && <WhatsAppInboxPage />}
      {activeTab === 'Auto-Reply' && <WhatsAppAutoReplyPage />}
      {activeTab === 'Flows' && <WhatsAppFlowsPage />}
      {activeTab === 'Forms' && <WhatsAppLeadFormsPage />}
      {activeTab === 'API Keys' && <WhatsAppApiKeysPage />}
      {activeTab === 'Interactive' && <WhatsAppInteractiveFlowsPage />}
      {activeTab === 'Analytics' && <WhatsAppAnalyticsPage />}

      {/* Legacy Meta-approved templates view (kept as reference but tab now renders new builder above) */}
      {false && activeTab === 'Templates' && (
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, color: theme.navy }}>Approved Templates</h3>
            <button onClick={fetchTemplates} style={{ ...btnStyle, marginTop: 0, padding: '8px 16px', fontSize: '0.82rem' }}>
              {templatesLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>

          {templates.length === 0 ? (
            <p style={{ color: theme.textMuted, fontSize: '0.9rem' }}>
              {templatesLoading ? 'Loading templates...' : 'No templates found. Make sure your WhatsApp Business API is configured.'}
            </p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#fafbfc' }}>
                  <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '0.78rem', color: theme.textMuted, fontWeight: '600', textTransform: 'uppercase', borderBottom: `1px solid ${theme.border}` }}>Name</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '0.78rem', color: theme.textMuted, fontWeight: '600', textTransform: 'uppercase', borderBottom: `1px solid ${theme.border}` }}>Status</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '0.78rem', color: theme.textMuted, fontWeight: '600', textTransform: 'uppercase', borderBottom: `1px solid ${theme.border}` }}>Language</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '0.78rem', color: theme.textMuted, fontWeight: '600', textTransform: 'uppercase', borderBottom: `1px solid ${theme.border}` }}>Category</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${theme.borderLight}` }}>
                    <td style={{ padding: '10px 14px', fontSize: '0.88rem', color: theme.navy }}>{t.name}</td>
                    <td style={{ padding: '10px 14px', fontSize: '0.88rem' }}>
                      <span style={{
                        padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600',
                        backgroundColor: t.status === 'APPROVED' ? '#d1fae5' : '#fef3c7',
                        color: t.status === 'APPROVED' ? '#065f46' : '#92400e',
                      }}>
                        {t.status}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: '0.88rem', color: theme.textMuted }}>{t.language}</td>
                    <td style={{ padding: '10px 14px', fontSize: '0.88rem', color: theme.textMuted }}>{t.category}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ─── SETTINGS ─── */}
      {activeTab === 'Settings' && (
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 16px', color: theme.navy }}>WhatsApp API Configuration</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label style={labelStyle}>Phone Number ID</label>
              <input
                type="text"
                value={settings.phoneNumberId || ''}
                onChange={e => saveSettings('phoneNumberId', e.target.value)}
                style={{ ...inputStyle, width: '100%' }}
                placeholder="Enter Phone Number ID"
              />
            </div>
            <div>
              <label style={labelStyle}>Access Token</label>
              <input
                type="password"
                value={settings.accessToken || ''}
                onChange={e => saveSettings('accessToken', e.target.value)}
                style={{ ...inputStyle, width: '100%' }}
                placeholder="Enter Access Token"
              />
            </div>
            <div>
              <label style={labelStyle}>Business Account ID</label>
              <input
                type="text"
                value={settings.businessAccountId || ''}
                onChange={e => saveSettings('businessAccountId', e.target.value)}
                style={{ ...inputStyle, width: '100%' }}
                placeholder="Enter Business Account ID"
              />
            </div>
            <div>
              <label style={labelStyle}>Verify Token</label>
              <input
                type="text"
                value={settings.verifyToken || ''}
                onChange={e => saveSettings('verifyToken', e.target.value)}
                style={{ ...inputStyle, width: '100%' }}
                placeholder="Enter Verify Token"
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', marginBottom: '28px' }}>
            <button
              onClick={persistSettings}
              disabled={settingsSaving}
              style={{ ...btnStyle, marginTop: 0, flex: 1, opacity: settingsSaving ? 0.6 : 1 }}
            >
              {settingsSaving ? 'Saving...' : '💾 Save to Server (Encrypted)'}
            </button>
            <button
              onClick={testConnection}
              disabled={testingConn}
              style={{ ...btnStyle, marginTop: 0, flex: 1, backgroundColor: theme.info || '#29A8E0', opacity: testingConn ? 0.6 : 1 }}
            >
              {testingConn ? 'Testing...' : '🔌 Test Connection'}
            </button>
          </div>

          <h3 style={{ margin: '0 0 16px', color: theme.navy, borderTop: `1px solid ${theme.border}`, paddingTop: '20px' }}>Auto-Send Toggles</h3>

          {[
            { key: 'autoAppointment', label: 'Auto send on Appointment Booked' },
            { key: 'autoTreatmentDone', label: 'Auto send on Treatment Done' },
            { key: 'autoTreatmentPlan', label: 'Auto send on Treatment Plan' },
            { key: 'autoPayment', label: 'Auto send on Payment Received' },
            { key: 'autoBirthday', label: 'Auto send Birthday Wishes' },
          ].map(item => (
            <div key={item.key} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 0', borderBottom: `1px solid ${theme.borderLight}`,
            }}>
              <span style={{ fontSize: '0.9rem', color: theme.navy, fontWeight: '500' }}>{item.label}</span>
              <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '26px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={autoSettings[item.key] !== false}
                  onChange={e => saveAutoSetting(item.key, e.target.checked)}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  backgroundColor: autoSettings[item.key] !== false ? theme.success : '#ccc',
                  borderRadius: '26px', transition: 'all 0.2s',
                }} />
                <span style={{
                  position: 'absolute', top: '3px',
                  left: autoSettings[item.key] !== false ? '25px' : '3px',
                  width: '20px', height: '20px', borderRadius: '50%',
                  backgroundColor: '#fff', transition: 'all 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WhatsAppPage;
